import React, { useState, useCallback } from 'react';
import Upload from 'lucide-react/dist/esm/icons/upload';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import ClipboardPaste from 'lucide-react/dist/esm/icons/clipboard-paste';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import {
  Modal, Button, TextField, TextArea, FileUpload,
  Alert, Spinner, ProgressBar, useToast,
} from '../../components/ui';
import { surveyApi } from '../../services/api/survey';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import type { SurveySummary, MappedAnswer } from './types';

type Step = 'input' | 'processing' | 'review' | 'error';
type InputMode = 'text' | 'file';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  survey: SurveySummary;
  onSuccess: () => void;
}

const SurveyUploadModal: React.FC<Props> = ({ isOpen, onClose, survey, onSuccess }) => {
  const { language } = useUIStore();
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => getTranslation(language, key, vars ?? {}),
    [language],
  );
  const { success: toastSuccess, error: toastError } = useToast();

  const [step, setStep] = useState<Step>('input');
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [pastedContent, setPastedContent] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [mappedAnswers, setMappedAnswers] = useState<MappedAnswer[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const resetState = useCallback(() => {
    setStep('input');
    setInputMode('text');
    setPastedContent('');
    setUploadedFile(null);
    setEmail('');
    setName('');
    setEmailError('');
    setNameError('');
    setConfidence(0);
    setMappedAnswers([]);
    setErrorMessage('');
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileUpload = useCallback((files: File[]) => {
    if (!files.length) return;
    setUploadedFile(files[0]);
  }, []);

  const validateFields = (): boolean => {
    let valid = true;
    if (!email.trim()) {
      setEmailError(t('surveyUploadEmailRequired'));
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError(t('surveyUploadEmailInvalid'));
      valid = false;
    } else {
      setEmailError('');
    }
    if (!name.trim()) {
      setNameError(t('surveyUploadNameRequired'));
      valid = false;
    } else {
      setNameError('');
    }
    return valid;
  };

  const contentReady = inputMode === 'file' ? !!uploadedFile : pastedContent.trim().length > 0;

  const handleProcess = useCallback(async () => {
    if (!validateFields()) return;
    if (!contentReady) return;

    setStep('processing');
    try {
      const payload: { content?: string; file?: File } = {};
      if (uploadedFile) payload.file = uploadedFile;
      else payload.content = pastedContent;

      const { data } = await surveyApi.mapAnswers(survey.id, payload);
      setConfidence(data.confidence);
      setMappedAnswers(data.mapped_answers);
      setStep('review');
    } catch (e: any) {
      setErrorMessage(e?.response?.data?.detail || e.message || t('surveyUploadProcessingFailed'));
      setStep('error');
    }
  }, [contentReady, pastedContent, uploadedFile, survey.id, t, email, name]);

  const doStore = useCallback(async () => {
    setSubmitting(true);
    try {
      await surveyApi.storeManualResponse(survey.id, {
        email: email.trim(),
        name: name.trim(),
        answers: mappedAnswers.map((a) => ({
          question_id: a.question_id,
          answer_text: a.answer_text,
        })),
      });
      toastSuccess(t('surveyUploadSuccessToast'));
      onSuccess();
      handleClose();
    } catch (e: any) {
      toastError(e?.response?.data?.detail || t('surveyUploadStoreFailed'));
      setSubmitting(false);
    }
  }, [survey.id, email, name, mappedAnswers, toastSuccess, toastError, t, onSuccess, handleClose]);

  const handleConfirm = useCallback(() => {
    doStore();
  }, [doStore]);

  const confidenceVariant = confidence >= 90 ? 'success' : confidence >= 70 ? 'warning' : 'danger';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header
        title={t('surveyUploadTitle')}
        icon={<Upload size={18} />}
        onClose={handleClose}
      />

      <Modal.Body>
        {step === 'input' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('surveyUploadLabelName')}
                placeholder={t('surveyUploadPlaceholderName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={nameError}
                required
              />
              <TextField
                label={t('surveyUploadLabelEmail')}
                placeholder={t('surveyUploadPlaceholderEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={emailError}
                required
              />
            </div>

            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-surface-secondary w-fit">
              <button
                type="button"
                onClick={() => { setInputMode('text'); setUploadedFile(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  inputMode === 'text'
                    ? 'bg-brand/15 text-primary'
                    : 'text-content-muted hover:text-content-secondary'
                }`}
              >
                <ClipboardPaste size={12} />
                {t('surveyUploadModePaste')}
              </button>
              <button
                type="button"
                onClick={() => { setInputMode('file'); setPastedContent(''); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  inputMode === 'file'
                    ? 'bg-brand/15 text-primary'
                    : 'text-content-muted hover:text-content-secondary'
                }`}
              >
                <FileText size={12} />
                {t('surveyUploadModeFile')}
              </button>
            </div>

            {inputMode === 'text' && (
              <TextArea
                label={t('surveyUploadLabelContent')}
                placeholder={t('surveyUploadPlaceholderContent')}
                value={pastedContent}
                onChange={(e) => setPastedContent(e.target.value)}
                rows={8}
              />
            )}

            {inputMode === 'file' && (
              <FileUpload
                onFiles={handleFileUpload}
                accept=".txt,.csv,.eml,.md,.xlsx,.xls,.doc,.docx,.pdf"
                multiple={false}
                maxSizeMB={10}
              >
                <div className="flex items-center justify-center gap-2 py-8 px-6">
                  <FileText size={16} className="text-content-faint" />
                  <span className="text-xs text-content-secondary">
                    {uploadedFile?.name || t('surveyUploadDropHint')}
                  </span>
                </div>
              </FileUpload>
            )}
          </div>
        )}

        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Spinner size="lg" />
            <p className="text-sm text-content-secondary">{t('surveyUploadProcessing')}</p>
          </div>
        )}

        {step === 'review' && (
          <div className="flex flex-col gap-4">
            {confidence < 70 && (
              <Alert variant="warning" icon={<AlertTriangle size={18} />}>
                {t('surveyUploadLowConfidence')}
              </Alert>
            )}

            <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-secondary">
              {confidence >= 70
                ? <CheckCircle size={20} className="text-status-success shrink-0" />
                : <AlertTriangle size={20} className="text-status-warning shrink-0" />
              }
              <div className="flex-1">
                <p className="text-sm font-medium text-content">
                  {t('surveyUploadConfidenceLabel', { value: confidence })}
                </p>
                <ProgressBar value={confidence} variant={confidenceVariant} size="sm" className="mt-1.5" />
              </div>
            </div>

            <div className="text-xs text-content-muted mb-1">
              {t('surveyUploadMappedCount', { count: mappedAnswers.length, total: survey.question_count })}
            </div>

            <div className="flex flex-col gap-3 max-h-[40vh] overflow-y-auto">
              {mappedAnswers.map((a, i) => (
                <div key={a.question_id} className="p-3 rounded-lg bg-surface-primary border border-subtle">
                  <p className="text-xs font-medium text-content-secondary mb-1">
                    Q{i + 1}: {a.question_text}
                  </p>
                  <p className="text-sm text-content">{a.answer_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="flex flex-col gap-4 py-4">
            <Alert variant="warning" icon={<AlertTriangle size={18} />}>
              {errorMessage}
              {confidence > 0 && (
                <span className="block mt-1 text-xs opacity-75">
                  {t('surveyUploadConfidenceLabel', { value: confidence })}
                </span>
              )}
            </Alert>
            <p className="text-sm text-content-secondary text-center">
              {t('surveyUploadTryDifferent')}
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 'input' && (
          <>
            <Button variant="ghost" onClick={handleClose}>{t('surveyUploadBtnCancel')}</Button>
            <Button
              variant="primary"
              onClick={handleProcess}
              disabled={!contentReady}
              leftIcon={<Upload size={14} />}
            >
              {t('surveyUploadBtnProcess')}
            </Button>
          </>
        )}

        {step === 'review' && (
          <>
            <Button variant="ghost" onClick={() => setStep('input')}>{t('surveyUploadBtnBack')}</Button>
            <Button
              variant={confidence < 70 ? 'outline' : 'success'}
              onClick={handleConfirm}
              loading={submitting}
              leftIcon={confidence < 70 ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
            >
              {confidence < 70 ? t('surveyUploadBtnProceedAnyway') : t('surveyUploadBtnConfirm')}
            </Button>
          </>
        )}

        {step === 'error' && (
          <>
            <Button variant="ghost" onClick={handleClose}>{t('surveyUploadBtnCancel')}</Button>
            <Button variant="secondary" onClick={() => setStep('input')}>
              {t('surveyUploadBtnTryAgain')}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(SurveyUploadModal);
